import { html, TemplateResult } from 'lit';
import { OutlineCard } from '../../base/outline-card/outline-card';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './idb-document-card.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import '../idb-icon/idb-icon';
import '../idb-heading/idb-heading';
import { LinkTargetType } from '../../base/outline-link/config';
import { HeadingLevelType } from 'src/components/base/outline-heading/config';
import { getUrlTarget } from '../../../utilities/url_utils';

/**The IDB card component.
 * @element idb-document-card
 * @property url — The property to set the href/url for the card
 * @property background-color — The property to set the background color of the card
 * @slot icon slot - The icon portion of the slot.
 * @slot heading slot - the title portion of the header
 * @slot subtitle slot - for the smaller heading under the title
 * @slot footer - The footer
 */

export type BackgroundColors = 'white' | 'gray';

@customElement('idb-document-card')
export class DocumentCard extends OutlineCard {
  static styles = [componentStyles];

  @property({ type: String, reflect: true, attribute: 'url' })
  url: string;

  @property({ type: String, attribute: 'button-target' })
  buttonTarget: LinkTargetType;

  /**
   * The heading tag to apply: p | span | h1 | h2 | h3 | h4 | h5 | h6
   */
  @property({ type: String, reflect: true, attribute: 'level' })
  level: HeadingLevelType = 'h3';

  /**
   * The color class to apply to the full card.
   */
  @property({ attribute: 'background-color' })
  backgroundColor: BackgroundColors;

  render(): TemplateResult {
    this.buttonTarget = getUrlTarget(this.url, this.buttonTarget);
    const cardClasses = {
      [`idb-document-card-bg-${this.backgroundColor}`]:
        this.backgroundColor !== undefined,
      'idb-document-card': true,
    };
    return html`
      <section class="${classMap(cardClasses)}">
        <a href="${this.url}" target="${this.buttonTarget}">
          <div class="tooltip">
            <span class="tooltip-text"><slot name="tooltip-text"></slot></span>
          </div>
          ${this._conditionalSlotTemplate({
            slotName: 'icon',
          })}
          <div class="header">
              <div class="titles">
                <div class="detail">
                  <span level="${this.level}"
                    >
                      ${this._conditionalSlotTemplate({
                        slotName: 'detail',
                      })}
                  </span>
                </div>
                <idb-heading level="${
                  this.level
                }" level-size="xl" level-style="bold" margin-size="0px"
                  >
                    ${this._conditionalSlotTemplate({
                      slotName: 'heading',
                    })}
                  </idb-heading
                >
              </div>
          </div>
          ${
            this.url
              ? html`
          <div class="footer">
            <div class="left-content">${this._conditionalSlotTemplate({
              slotName: 'subtitle',
            })}</div>
            <div class="right-content">
              <span>${this._conditionalSlotTemplate({
                slotName: 'cta',
              })}</span> <idb-icon class="icon" icon="download"></idb-icon
            </div>
          </div>`
              : ''
          }
        </a>
      </section>
    `;
  }
}
